const ROW_FIELDS = new Set([
  'strike', 'optionType', 'cumulativeGammaNet', 'cumulativeDeltaNet',
  'totalNetVol', 'accumulatedVolume',
]);

const EXPOSURE_FIELDS = new Set(['greek_type', 'timestamp', 'net_exposure']);
const LEVEL_FIELDS = new Set(['level_type', 'timestamp', 'strike_price']);
const TOP_OPTION_FIELDS = new Set(['greek_type', 'option_type', 'rank', 'strike_price', 'timestamp']);
const GTS_FIELDS = new Set(['trend_type', 'timestamp', 'value']);
const INDEX_SYMBOLS = ['SPX', 'ES'];

function pick(obj, fields) {
  const out = {};
  for (const k of fields) if (k in obj) out[k] = obj[k];
  return out;
}

function pickList(list, fields) {
  return (list ?? []).map((entry) => pick(entry, fields));
}

function pickIndexData(indexData = {}) {
  const out = {};
  for (const symbol of INDEX_SYMBOLS) out[symbol] = indexData?.[symbol] ?? [];
  return out;
}

function pickAggregationState(aggregationState = {}) {
  const out = {};
  for (const symbol of INDEX_SYMBOLS) {
    const state = aggregationState?.[symbol];
    if (state && typeof state === 'object') out[symbol] = { ...state };
  }
  return out;
}

function trimRows(rawRows, spot, strikeWindow) {
  const lo = spot - strikeWindow;
  const hi = spot + strikeWindow;
  const trimmedRows = {};
  for (const [sym, row] of Object.entries(rawRows ?? {})) {
    const strike = Number(row.strike ?? 0);
    if (strike < lo || strike > hi) continue;
    trimmedRows[sym] = pick(row, ROW_FIELDS);
  }
  return trimmedRows;
}

function clipToFrame(list, frameTs, dayWindowStartSec) {
  return (list ?? []).filter((entry) =>
    Number(entry.timestamp) >= dayWindowStartSec && Number(entry.timestamp) <= frameTs);
}

function trimFrameHeatmap(frame, strikeWindow, dayWindowStartSec) {
  const spot = frame.options.spot ?? 0;
  const frameTs = frame.t / 1000;
  const gammaOnly = (frame.greeks?.greekExposures?.spx ?? []).filter((entry) => entry?.greek_type === 'gamma');

  return {
    t: frame.t,
    options: { rowsBySym: trimRows(frame.options.rowsBySym, spot, strikeWindow), spot },
    greeks: {
      greekExposures: { spx: pickList(clipToFrame(gammaOnly, frameTs, dayWindowStartSec), EXPOSURE_FIELDS) },
      dynamicLevels: { spx: pickList(frame.greeks?.dynamicLevels?.spx, LEVEL_FIELDS) },
      gexTrendStrength: { spx: [] },
      topOptions: { spx: [] },
      adaptiveTopOptions: { spx: [] },
      adaptiveDynamicLevels: { spx: [] },
    },
    indexData: { SPX: [], ES: [] },
    aggregationState: pickAggregationState(frame.aggregationState),
  };
}

function trimFrameFull(frame, strikeWindow, dayWindowStartSec) {
  const spot = frame.options.spot ?? 0;
  const frameTs = frame.t / 1000;

  return {
    t: frame.t,
    options: { rowsBySym: trimRows(frame.options.rowsBySym, spot, strikeWindow), spot },
    greeks: {
      greekExposures: {
        spx: pickList(clipToFrame(frame.greeks?.greekExposures?.spx, frameTs, dayWindowStartSec), EXPOSURE_FIELDS),
      },
      dynamicLevels: { spx: pickList(frame.greeks?.dynamicLevels?.spx, LEVEL_FIELDS) },
      gexTrendStrength: {
        spx: pickList(clipToFrame(frame.greeks?.gexTrendStrength?.spx, frameTs, dayWindowStartSec), GTS_FIELDS),
      },
      topOptions: {
        spx: pickList(clipToFrame(frame.greeks?.topOptions?.spx, frameTs, dayWindowStartSec), TOP_OPTION_FIELDS),
      },
      adaptiveTopOptions: {
        spx: pickList(
          clipToFrame(frame.greeks?.adaptiveTopOptions?.spx, frameTs, dayWindowStartSec),
          TOP_OPTION_FIELDS,
        ),
      },
      adaptiveDynamicLevels: {
        spx: pickList(frame.greeks?.adaptiveDynamicLevels?.spx, LEVEL_FIELDS),
      },
    },
    indexData: pickIndexData(frame.indexData),
    aggregationState: pickAggregationState(frame.aggregationState),
  };
}

const TRIMMERS = {
  full: trimFrameFull,
  heatmap: trimFrameHeatmap,
};

export function trimPlaybackFixture(rawFixture, { mode = 'full', strikeWindow = 150, beforeEt = null } = {}) {
  const trimFn = TRIMMERS[mode];
  if (!trimFn) throw new Error(`Unknown trim mode "${mode}".`);

  let frames = Array.isArray(rawFixture) ? rawFixture : rawFixture?.frames ?? [];
  if (!frames.length) throw new Error('No frames found in fixture.');

  if (beforeEt) {
    const [hh, mm] = beforeEt.split(':').map(Number);
    frames = frames.filter((frame) => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false,
      }).formatToParts(new Date(frame.t));
      const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
      const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);
      return hour * 60 + minute < hh * 60 + mm;
    });
  }

  if (!frames.length) throw new Error('No frames left after trimming.');

  const dayWindowStartSec = frames[frames.length - 1].t / 1000 - 24 * 3600;

  return {
    version: rawFixture?.version ?? 1,
    recordedAt: rawFixture?.recordedAt ?? frames[0].t,
    cadenceMs: rawFixture?.cadenceMs ?? 15_000,
    mode,
    frames: frames.map((frame) => trimFn(frame, strikeWindow, dayWindowStartSec)),
  };
}
